"use client";

import { useEffect, useSyncExternalStore } from "react";
import { useSearchParams } from "next/navigation";
import Link from "@/components/ui/LocaleLink";

/**
 * The `?next=` return path for the auth forms.
 *
 * Two constraints, each measured: (1) the FORM must not sit inside a Suspense
 * boundary that resolves after paint, or it remounts and drops what the
 * reader typed; (2) reading `window.location` once is wrong for an in-app
 * navigation, where the App Router renders the page BEFORE it updates the
 * address bar, so `?next` read empty (the profile's "sign in to contact"
 * link lost its return path, 2026-10-14). So the form reads a tiny store, and
 * `NextParamBridge`, mounted in its OWN `<Suspense fallback={null}>`, feeds it
 * from `useSearchParams()`, which follows router navigations. The server
 * still validates `next` (loginAction, /auth/callback).
 */

let current: string | undefined;
const listeners = new Set<() => void>();
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useNextParam(): string | undefined {
  return useSyncExternalStore(subscribe, () => current, () => undefined);
}

/** Mount once per auth page, inside `<Suspense fallback={null}>`. Renders nothing. */
export function NextParamBridge() {
  const next = useSearchParams().get("next") ?? undefined;
  useEffect(() => {
    if (next !== current) {
      current = next;
      listeners.forEach((l) => l());
    }
    // Cleared on leaving the page, so the next auth page never renders with
    // a stale return path, even for one frame.
    return () => { current = undefined; };
  }, [next]);
  return null;
}

/** A link to another auth page that carries `?next=` across the swap. */
export function AuthSwapLink({ href, className, children }: { href: "/login" | "/signup"; className: string; children: React.ReactNode }) {
  const next = useNextParam();
  return (
    <Link href={next ? `${href}?next=${encodeURIComponent(next)}` : href} className={className}>
      {children}
    </Link>
  );
}
